import { FunctionComponent, useState, useEffect } from 'react'
import DashboardLayout from '../../components/layout/layout'
import { Alert, Box, Button, Divider, Grid, IconButton, Paper, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { Add, Remove } from '@mui/icons-material';
import StorageService from '../../services/storageService';
import { Address } from './online-order-entry-header';
import { useDeleteChannelPartnerOEntryHeaderRemoveCart, useGetChannelPartnerOEntryHeaderCart, useGetChannelPartnerOEntryHeaderProductDetail, usePatchChannelPartnerOEntryHeaderCart } from '../../hooks/api/oms';
import { StyledTextField } from '../../components/ui/formInput';
import ProductSearchAutocomplete from '../../components/ProductSearch/ProductSearchAutocomplete';
import moment from 'moment';
import React from 'react';

const TableCol = styled(TableCell)(({ theme }) => ({
  border: '1px solid rgba(224, 224, 224, 1)',
}));


type ValidateField = {
  invalid: boolean;
  error: string;
};

type ValidateFieldsState = {
  partNumber: ValidateField;
  quantity: ValidateField;
};

const OnlineOrderEntryOrder: FunctionComponent = () => {
  const navigate = useNavigate();
  const oEntryHeader = StorageService.getOEntryHeader();
  const oEntryHeaderValue = StorageService.getOEntryHeaderValue();
  const oEntryHeaderCart = StorageService.getOEntryHeaderCartValue();
  const {data: getCart, refetch } = useGetChannelPartnerOEntryHeaderCart();
  const { mutateAsync: getHeaderProductDetail } = useGetChannelPartnerOEntryHeaderProductDetail();
  const { mutateAsync: updateCartItem } = usePatchChannelPartnerOEntryHeaderCart();
  const { mutateAsync: deleteCartItem } = useDeleteChannelPartnerOEntryHeaderRemoveCart();
  const [disabled, setDisabled] = useState<boolean>(false);

  const [dateValues, setDateValues] = useState<Record<string, string>>({});
  const [quantityValues, setQuantityValues] = useState<Record<string, string>>({});
  const [quantityInputValues, setQuantityInputValues] = useState<Record<string, string>>({});
  const [commentValues, setCommentValues] = useState<Record<string, string>>({});

  const handleDateChange = (id: string, value: string) => {
    if (value !== '') {
      setError('');
      setDateValues((prev) => ({
        ...prev,
        [id]: value,
      }));
      
      // Also save to localStorage for persistence
      const cartShipDates = JSON.parse(localStorage.getItem('cart_ship_dates') || '{}');
      cartShipDates[id] = value;
      localStorage.setItem('cart_ship_dates', JSON.stringify(cartShipDates));
    }
  };

  const handleCommentChange = (id: string, value: string) => {
    setError('');
    // Convert to uppercase and limit to 68 characters
    const uppercaseValue = value.toUpperCase().slice(0, 68);
    
    setCommentValues((prev) => ({
      ...prev,
      [id]: uppercaseValue,
    }));
    
    // Also save to localStorage for persistence
    const cartComments = JSON.parse(localStorage.getItem('cart_comments') || '{}');
    cartComments[id] = uppercaseValue;
    localStorage.setItem('cart_comments', JSON.stringify(cartComments));
  };

  const handleQuantityUpdate = (id: string, value: string) => {
    // Update the input display value
    setQuantityInputValues((prev) => ({
      ...prev,
      [id]: value,
    }));

    // Allow empty string for user to clear and type
    if (value === '') {
      setError('');
      // Don't update quantityValues for empty string
      return;
    }

    const num = Number(value);

    if (isNaN(num)) {
      setError('Please enter a valid number.');
      // Don't update quantityValues for invalid input
      return;
    }

    if (!Number.isInteger(num)) {
      setError('Number must be a whole number (no decimals).');
      // Don't update quantityValues for non-integers
      return;
    }

    if (num <= 0) {
      setError('Number must be greater than zero.');
      // Don't update quantityValues for zero or negative
      return;
    }

    // Only update quantityValues if validation passes
    setQuantityValues((prev) => ({
      ...prev,
      [id]: value,
    }));
    setError('');
  };

  const [partNumber, setPartNumber] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [quantityInput, setQuantityInput] = useState('1');
  const [error, setError] = useState<string>('');

  const formValidation: ValidateFieldsState = {
    partNumber: { invalid: false, error: '' },
    quantity: { invalid: false, error: '' }
  };
  const [validateFields, setValidateFields] = useState<ValidateFieldsState>(formValidation);

  const validateForm = () => {
    let valid = true;
    const errors = { ...validateFields };

    if (!partNumber?.trim()) {
      errors.partNumber = { invalid: true, error: 'Part number is required' };
      valid = false;
    } else {
      errors.partNumber = { invalid: false, error: '' };
    }

    if (quantity < 1) {
      errors.quantity = { invalid: true, error: 'Quantity must be at least 1' };
      valid = false;
    } else {
      errors.quantity = { invalid: false, error: '' };
    }

    setValidateFields(errors);
    return valid;
  }

  const shipToAddresses = oEntryHeader?.shipToAddresses?.find((address: Address) => address.ad_addr === oEntryHeaderValue?.shipTo) || {};
  const shipVia = oEntryHeader?.shipViaOptions?.find((option: any) => option.fr_list === oEntryHeaderValue?.shipViaCarrier) || {};
  const shippingTerms = oEntryHeader?.shipTermsOptions?.find((term: any) => term.ft_terms === oEntryHeaderValue?.shippingTerms) || {};

  const handleQuantityChange = (delta: number) => {
    const newQuantity = Math.max(1, quantity + delta);
    setQuantity(newQuantity);
    setQuantityInput(newQuantity.toString());
  };

  const handleQuantityInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setQuantityInput(value);
    
    if (value === '') {
      return; // Allow empty field
    }
    
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 1) {
      setQuantity(numValue);
    }
  };

  const handleQuantityBlur = () => {
    if (quantityInput === '' || isNaN(parseInt(quantityInput)) || parseInt(quantityInput) < 1) {
      setQuantityInput('1');
      setQuantity(1);
    }
  };

  const handleProductSelect = (product: any) => {
    setPartNumber(product.partNumber);
    // Clear any previous validation errors
    setValidateFields(prev => ({
      ...prev,
      partNumber: { invalid: false, error: '' }
    }));
  };

  const handleNext = () => {
    if (getCart?.items?.length === 0) {
      setError('Order must contain at least one item.');
      return;
    }
    // if (getCart?.subtotal < 250) {
    //   setError('Cart total must be at least $250 to proceed.');
    //   return;
    // }
    setDisabled(true);
    navigate('/order-management-system/online-order-entry/order/send');
  };

  const handleAdd = async () => {
    setValidateFields(formValidation);
    if (!validateForm()) {
      return;
    }
    setError('');
    setDisabled(true);
    StorageService.removeOEntryHeaderSetPointValue();
    try {
      const result = await getHeaderProductDetail({
        partNumber: partNumber,
        quantity: quantity,
      })
      if (result?.status === 200) {
        setDisabled(false);
        navigate(`/order-management-system/online-order-entry/add-product/${partNumber}/${quantity}`);
      } else {
        setDisabled(false);
        setError(result?.data?.message || 'An error occurred during login. Please try again.');
        setTimeout(() => {
          setError('');
        }, 3000);
      }
    } catch (err: any) {
      setDisabled(false);
      if (err?.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError('An error occurred during login. Please try again.');
      }
    }
  };

  const handleCancel = () => {
    navigate('/order-management-system/online-order-entry/order/cancel');
  };


  const handleDelete = async (partNumber: string) => {
    setError('');
    setDisabled(true);
    
    try {
      const result = await deleteCartItem({
        partNumber: partNumber,
      });
      
      if (result?.status === 200) {
        setDisabled(false);
        await refetch();
      } else {
        setDisabled(false);
        setError(result?.data?.message || 'An error occurred during delete. Please try again.');
        setTimeout(() => {
          setError('');
        }, 3000);
      }
    } catch (err: any) {
      setDisabled(false);
      if (err?.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError('An error occurred during delete. Please try again.');
      }
    }
  };

  const handleUpdate = async (partNumber: string) => {
    setError('');
    setDisabled(true);
    
    // Check if there are changes for this specific part number
    const hasDateChanges = dateValues[partNumber]?.trim();
    const hasQuantityChanges = quantityValues[partNumber]?.trim();
    const hasCommentChanges = commentValues[partNumber]?.trim();
    
    // Validate comment length
    if (hasCommentChanges && commentValues[partNumber].length > 68) {
      setDisabled(false);
      setError(`Comment cannot exceed 68 characters (current: ${commentValues[partNumber].length})`);
      setTimeout(() => {
        setError('');
      }, 3000);
      return;
    }
    
    // Check if there's a validation error for this part number
    const currentInput = quantityInputValues[partNumber];
    if (currentInput !== undefined && currentInput !== '') {
      const num = Number(currentInput);
      if (isNaN(num) || !Number.isInteger(num) || num <= 0) {
        setDisabled(false);
        if (num <= 0) {
          setError('Number must be greater than zero.');
        } else if (!Number.isInteger(num)) {
          setError('Number must be a whole number (no decimals).');
        } else {
          setError('Please enter a valid number.');
        }
        setTimeout(() => {
          setError('');
        }, 3000);
        return;
      }
    }
    
    if (hasDateChanges || hasQuantityChanges || hasCommentChanges) {
      // Use user-entered date if available - don't clear it based on order cap
      // User-entered dates should be preserved and saved even when order cap exceeded
      let shipDateToSend = dateValues[partNumber]?.trim();
      
      // REMOVED: Logic that clears shipDateToSend when order cap exceeded
      // User-entered dates should be saved to backend regardless of order cap status
      
      const payload = {
        partNumber: partNumber,
        ...(shipDateToSend && { requestedShipDate: moment(shipDateToSend, "YYYY-MM-DD").format("MM/DD/YY") }),
        ...(quantityValues[partNumber]?.trim() && { quantity: Number(quantityValues[partNumber]) }),
        ...(commentValues[partNumber]?.trim() && { comment: commentValues[partNumber] }),
      }
      try {
        const result = await updateCartItem(payload);
        if (result?.status === 200) {
          setDisabled(false);
          setDateValues((prev) => {
            const newValues = { ...prev };
            delete newValues[partNumber];
            return newValues;
          });
          setQuantityValues((prev) => {
            const newValues = { ...prev };
            delete newValues[partNumber];
            return newValues;
          });
          setQuantityInputValues((prev) => {
            const newValues = { ...prev };
            delete newValues[partNumber];
            return newValues;
          });
          setCommentValues((prev) => {
            const newValues = { ...prev };
            delete newValues[partNumber];
            return newValues;
          });
          
          // Clear localStorage comment after successful update
          const cartComments = JSON.parse(localStorage.getItem('cart_comments') || '{}');
          delete cartComments[partNumber];
          localStorage.setItem('cart_comments', JSON.stringify(cartComments));
          
          // Clear localStorage ship date after successful update
          const cartShipDates = JSON.parse(localStorage.getItem('cart_ship_dates') || '{}');
          delete cartShipDates[partNumber];
          localStorage.setItem('cart_ship_dates', JSON.stringify(cartShipDates));
          
          await refetch();
        } else {
          setDisabled(false);
          // Handle validation errors from API
          if (result?.data?.errors && Array.isArray(result.data.errors) && result.data.errors.length > 0) {
            // Display validation errors (like ship date validation)
            setError(result.data.errors.join(', '));
          } else {
            setError(result?.data?.message || 'An error occurred during update. Please try again.');
          }
          setTimeout(() => {
            setError('');
          }, 5000); // Show error for 5 seconds
        }
      } catch (err: any) {
        setDisabled(false);
        // Handle validation errors from API response
        if (err?.response?.data?.errors && Array.isArray(err.response.data.errors) && err.response.data.errors.length > 0) {
          setError(err.response.data.errors.join(', '));
        } else if (err?.response?.data?.error) {
          let errorMessage = err.response.data.error;
          
          // COMMENTED OUT: Order capacity error handling - matches production behavior (AddProduct2.asp)
          // Production system allows quantities above order cap without showing error messages
          // Order cap exceeded items are allowed to proceed without user notification
          /*
          // If it's an order capacity error, include the order capacity value
          if (err.response.data.error === 'Quantity exceeds order capacity' && err.response.data.orderCap) {
            errorMessage = `Part ${partNumber}: Quantity exceeds order capacity. Maximum allowed: ${err.response.data.orderCap}`;
          }
          */
          
          setError(errorMessage);
        } else if (err?.response?.data?.message) {
          setError(err.response.data.message);
        } else if (err?.message) {
          setError(err.message);
        } else {
          setError('An error occurred during update. Please try again.');
        }
        setTimeout(() => {
          setError('');
        }, 5000); // Show error for 5 seconds
      }
    } else {
      setDisabled(false);
      setError('No changes to update. Please modify comment, date, or quantity first.');
      setTimeout(() => {
        setError('');
      }, 3000);
    }
  }

  const formatDate = (date: string) => {
    return (date ? moment(date, "MM/DD/YY").format("YYYY-MM-DD") : '');
  }

  useEffect(() => {
    // Clear old localStorage data that might have inconsistent date formats
    localStorage.removeItem('cart_ship_dates');
    
    if (getCart?.items) {
      const initialCommentValues: Record<string, string> = {};
      getCart.items.forEach((item: any) => {
        // Check multiple possible comment field names from API
        const apiComment = item.comment || item.comments || item.userComment || item.note || '';
        
        // Also check localStorage as fallback
        const localStorageComments = JSON.parse(localStorage.getItem('cart_comments') || '{}');
        const localStorageComment = localStorageComments[item.partNumber] || '';
        
        // Use API comment if available, otherwise fallback to localStorage
        const commentValue = apiComment || localStorageComment;
        if (commentValue) {
          initialCommentValues[item.partNumber] = commentValue;
        }
      });
      setCommentValues(initialCommentValues);
    }
  }, [getCart]);

  return (
    <div className="online-order-entry-page">
      <DashboardLayout title={"Orders Management"} initialBreadcrumbs={"Channel Partners"} levelBreadcrumbs={"Online Order Entry"} breadcrumbs={'Order'} activeMenu={'oms'}>
      <Box sx={{ backgroundColor: '#f5f5f5', border: '1px solid #e5e5e5' }}>
        <Grid container sx={{backgroundColor: '#c3183c', color: '#ffffff', padding: '10px 20px'}}>
          <Grid size={8}>
            <Typography variant="h6" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>Order Information</Typography>
          </Grid>
          <Grid size={4} sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center'}}>
            <Typography sx={{ fontSize: '0.875rem', borderBottom: '2px solid #e5496a', cursor: 'pointer' }} onClick={() => navigate('/order-management-system/online-order-entry/header')}>
              Edit
            </Typography>
          </Grid>
        </Grid>
        <Grid container sx={{padding: '10px 20px'}}>
          <Grid size={12}>
            <Typography sx={{ fontSize: '0.875rem', color: '#000000' }}>Purchase Order No : {oEntryHeaderValue?.purchaseOrderNumber}</Typography>
          </Grid>
        </Grid>
        <Divider sx={{ mt: 0 }} />

        <Grid container sx={{padding: '10px 20px'}}>
          <Grid size={6}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold', color: '#000000' }}>Sold To:</Typography>
            <Typography sx={{ fontSize: '0.875rem', color: '#000000' }}>
              {oEntryHeader?.billToInfo?.ad_name}
              <br />
              {`${oEntryHeader?.billToInfo?.ad_line1} ${oEntryHeader?.billToInfo?.ad_state} ${oEntryHeader?.billToInfo?.ad_zip}`}
            </Typography>
          </Grid>
          <Grid size={6}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold', color: '#000000' }}>Ship To:</Typography>
            <Typography sx={{ fontSize: '0.875rem', color: '#000000' }}>
              {shipToAddresses?.ad_name}
              <br />
              {`${shipToAddresses?.ad_line1} ${shipToAddresses?.ad_city}, ${shipToAddresses?.ad_state} ${shipToAddresses?.ad_zip}`}
            </Typography>
          </Grid>
        </Grid>
        <Divider sx={{ mt: 0 }} />

        <Grid container sx={{padding: '10px 20px'}}>
          <Grid size={12}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold', color: '#000000' }}>Shipping Details:</Typography>
            <Grid container spacing={2} mt={1}>
              <Grid size={4}>
                <Typography sx={{ fontSize: '0.875rem', color: '#000000' }}>Ship Via Carrier: {shipVia?.fr_desc}</Typography>
              </Grid>
              <Grid size={4}>
                <Typography sx={{ fontSize: '0.875rem', color: '#000000' }}>Shipping Terms: {shippingTerms?.ft_desc}</Typography>
              </Grid>
              {/* <Grid size={4}>Carrier Account No. {oEntryHeaderValue?.carrierAccountNo}</Grid> */}
              
              {/* Show carrier account number only if it's present and filled */}
              {oEntryHeaderValue?.carrierAccountNo && oEntryHeaderValue.carrierAccountNo.trim() !== '' && (
                <Grid size={4}>
                  <Typography sx={{ fontSize: '0.875rem', color: '#000000' }}>Carrier Account No. {oEntryHeaderValue.carrierAccountNo}</Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Divider sx={{ mt: 0 }} />

        {/* Only show Standard Comments section when there are actual comments or checkboxes checked */}
        {(oEntryHeader?.customerComments?.length > 0 || 
          oEntryHeaderValue?.comments?.oe || 
          oEntryHeaderValue?.comments?.doNotPartial || 
          oEntryHeaderValue?.comments?.expedite || 
          oEntryHeaderValue?.comments?.con) && (
          <Grid container sx={{padding: '10px 20px'}}>
            <Grid size={12}>
              <Grid size={12} sx={{ fontSize: '0.875rem', fontWeight: 'bold', color: '#000000' }}>STANDARD COMMENTS:</Grid>
              {/* Show checkbox-selected standard comments first (matches legacy order) */}
              <>
                {
                  oEntryHeaderValue?.comments?.doNotPartial && (
                    <Typography sx={{ fontSize: '0.875rem', color: '#000000', mt: 1 }}>
                      DO NOT PARTIAL (SHIP ORDER WHEN ALL LINE ITEMS ARE AVAILABLE)  
                    </Typography>
                  )
                }
                {
                  oEntryHeaderValue?.comments?.expedite && (
                    <Typography sx={{ fontSize: '0.875rem', color: '#000000', mt: 1 }}>
                      EXPEDITE ORDER/RSS (ADDITIONAL CHARGE: GREATER OF $100 OR 20% OF ORDER)
                    </Typography>
                  )
                }
                {
                  oEntryHeaderValue?.comments?.con && (
                    <Typography sx={{ fontSize: '0.875rem', color: '#000000', mt: 1 }}>
                      CONSOLIDATE ORDER ON {oEntryHeaderValue?.selectDay?.toUpperCase()}
                    </Typography>
                  )
                }
              </>
              
              {/* Show OE customer comments after checkbox comments (matches legacy) */}
              {
                oEntryHeaderValue?.comments?.oe && oEntryHeader?.customerComments?.length > 0 && oEntryHeader?.customerComments?.map((comment: Record<string, string>, index: number) => (
                  Array.from({ length: 15 }, (_, i) => {
                    const key = `cmt_cmmt${i + 1}`;
                    const value = comment[key];
                    return value && value.trim() !== '.' ? (
                      <Grid size={12} key={`${index}-${i}`}>
                        <Typography sx={{ fontSize: '0.875rem', color: '#000000', mb: 1 }}>
                          {value}
                        </Typography>
                      </Grid>
                    ) : null;
                  })
                ))
              }
            </Grid>
          </Grid>
        )}
        {oEntryHeaderValue?.otherComments && oEntryHeaderValue.otherComments !== "** Enter comments here if not listed in standard comments above **\n**         Please do not enter unnecessary comments.         **" && (
          <Grid container sx={{padding: '5px 20px 10px 20px'}}>
            <Grid size={12}>
              <Grid size={12} sx={{ fontSize: '0.875rem', fontWeight: 'bold', color: '#000000' }}>OTHER COMMENTS:</Grid>
              <Typography sx={{ fontSize: '0.875rem', color: '#000000', mt: 1, whiteSpace: 'pre-line' }}>
                {oEntryHeaderValue.otherComments.toUpperCase()}
              </Typography>
            </Grid>
          </Grid>
        )}
      </Box>
      {
        getCart?.items?.length > 0 && (
          <Grid container sx={{padding: '10px 0px 0px 0px', mt: 1}}>
            <TableContainer component={Paper} variant="outlined" sx={{borderRadius: 0}}>
              <Table size="small" aria-label="inventory list table">
                <TableHead sx={{ backgroundColor: "#c3183c"}}>
                  <TableRow>
                    {['Part Number', 'Description', 'Request Date', 'Quantity', 'List Price', 'Extended List Price', 'Action'].map((heading) => (
                      <TableCol key={heading} sx={{ color: '#fff', fontWeight: 'bold' }}>
                        {heading}
                      </TableCol>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getCart.items.map((item: any, index: number) => (
                    <React.Fragment key={index}>
                      <TableRow>
                        <TableCol>{item.partNumber}</TableCol>
                        <TableCol>{item.description}</TableCol>
                        <TableCol>
                          <StyledTextField
                            label=""
                            value={
                              (() => {
                                // Priority 1: User-entered date (dateValues) - always show if user entered it
                                // This allows user-entered dates to show even when order cap exceeded
                                if (dateValues[item.partNumber]) {
                                  return dateValues[item.partNumber];
                                }
                                
                                // Priority 2: API response (saved date from backend) - check BEFORE order cap
                                // This ensures saved dates from backend are shown even when order cap exceeded
                                if (item.requestedShipDate) {
                                  return formatDate(item.requestedShipDate);
                                }
                                
                                // Priority 3: Check if quantity exceeds order cap - if so, show blank ship date (initial state)
                                // Only show empty if no user-entered date AND no saved date from backend
                                const currentQuantity = quantityInputValues[item.partNumber] !== undefined 
                                  ? Number(quantityInputValues[item.partNumber]) 
                                  : (quantityValues[item.partNumber] ? Number(quantityValues[item.partNumber]) : item.quantity);
                                
                                if (item.orderCap && item.orderCap > 0 && currentQuantity > item.orderCap) {
                                  return ''; // Blank ship date when order cap exceeded (only if no saved date)
                                }
                                
                                return '';
                              })()
                            }
                            type={'date'}
                            onChange={(e: any) => handleDateChange(item.partNumber, e.target.value)}
                          />
                        </TableCol>
                        <TableCol>
                          <StyledTextField
                            label=""
                            value={
                              quantityInputValues[item.partNumber] !== undefined 
                                ? quantityInputValues[item.partNumber] 
                                : (quantityValues[item.partNumber] || item.quantity)
                            }
                            type={'number'}
                            inputProps={{
                              min: 1,
                            }}
                            onChange={(e: any) => handleQuantityUpdate(item.partNumber, e.target.value)}
                          />
                        </TableCol>
                        <TableCol>${parseFloat(item.listPrice).toFixed(2)}</TableCol>
                        <TableCol>${(item.listPrice * item.quantity) > 1000 ? (item.listPrice * item.quantity).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : (item.listPrice * item.quantity).toFixed(2)}</TableCol>
                        <TableCol>
                          <Button disabled={disabled} variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 15px !important', marginRight: '5px' }} onClick={(e) => handleUpdate(item.partNumber)}>
                            UPDATE
                          </Button>
                          <Button disabled={disabled} variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 15px !important' }} onClick={(e) => handleDelete(item.partNumber)}>
                            DELETE
                          </Button>
                        </TableCol>
                      </TableRow>
                      
                      {/* Comment row below each product row */}
                      <TableRow>
                        <TableCol colSpan={8} sx={{ 
                          border: 'none', 
                          padding: '8px 20px', 
                          backgroundColor: '#f8f9fa',
                          borderTop: '1px solid #e9ecef'
                        }}>
                          <Grid container alignItems="center" spacing={2}>
                            <Grid size={2} sx={{ 
                              fontWeight: 'bold', 
                              color: '#495057',
                              fontSize: '0.875rem'
                            }}>
                              Comment:
                            </Grid>
                            <Grid size={10}>
                              <StyledTextField
                                label=""
                                value={commentValues[item.partNumber] || ''}
                                placeholder="Add or edit comment..."
                                onChange={(e: any) => handleCommentChange(item.partNumber, e.target.value)}
                                inputProps={{
                                  maxLength: 68
                                }}
                                sx={{
                                  '& .MuiInputBase-root': {
                                    fontSize: '0.875rem',
                                    minHeight: '32px'
                                  }
                                }}
                              />
                              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block', fontSize: '0.75rem' }}>
                                {(commentValues[item.partNumber] || '').length}/68 characters
                              </Typography>
                            </Grid>
                          </Grid>
                        </TableCol>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>    
        )
      }
               <Grid container sx={{backgroundColor: '#c3183c', color: '#ffffff', padding: '10px 20px', mt: oEntryHeaderCart?.length ? 0 : 1}}>
                         <Grid size={4}>
            <Grid size={12} sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Product</Grid>
                       <Box sx={{ mt: 1, pr: 1 }}>
              <ProductSearchAutocomplete
                value={partNumber}
                onChange={setPartNumber}
                onSelect={handleProductSelect}
                placeholder="Search by part number"
                label=""
                size="small"
              />
            </Box>
           {validateFields.partNumber.invalid && (
             <Typography sx={{ fontSize: '0.875rem', color: 'white', mt: 0.5, display: 'block' }}>
               {validateFields.partNumber.error}
             </Typography>
           )}
         </Grid>

           <Grid size={2}>
           <Grid size={12} sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Quantity</Grid>
          <Box display="flex" alignItems="center" bgcolor="white" borderRadius={1} sx={{ mt: 1 }}>
            <IconButton onClick={() => handleQuantityChange(-1)} size="small">
              <Remove />
            </IconButton>
            <TextField
              value={quantityInput}
              onChange={handleQuantityInputChange}
              onBlur={handleQuantityBlur}
              size="small"
              type="number"
              inputProps={{
                min: 1,
              }}
              sx={{ 
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0,
                  "& fieldset": {
                    top: '-5.5px',
                    borderTop: 'none',
                    borderBottom: 'none',
                    borderColor: '#000000',
                  },
                  "&:hover fieldset": {
                    borderColor: '#000000',
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: '#000000',
                    borderWidth: 1,
                  },
                  "& input": {
                    textAlign: 'center',
                  },
                }
              }}
            />
            <IconButton onClick={() => handleQuantityChange(1)} size="small">
              <Add />
            </IconButton>
          </Box>
        </Grid>

        <Grid size={2}>
          <Box sx={{ mt: 1 }}>
            <Button variant="contained" sx={{
              bgcolor: 'white',
              color: '#c3183c',
              fontWeight: 'bold',
              marginTop: '20px',
              height: '40px',
              marginLeft: '20px',
              '&:hover': { bgcolor: '#f8f8f8' }
            }}
            onClick={handleAdd}>
              ADD
            </Button>
          </Box>
        </Grid>

                                   <Grid size={4} sx={{ textAlign: 'right', pr: 2 }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold', mb: 1 }}>Order Sub Total</Typography>
            <TextField
              value={`$${parseFloat(getCart?.subtotal || '0') > 1000 ? parseFloat(getCart?.subtotal || '0').toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : parseFloat(getCart?.subtotal || '0').toFixed(2)}`}
              size="small"
              InputProps={{ 
                readOnly: true,
                sx: { textAlign: 'right' }
              }}
              sx={{ 
                width: '120px',
                bgcolor: 'white', 
                borderRadius: 1,
                '& .MuiInputBase-input': {
                  textAlign: 'right',
                  pr: 1
                }
              }}
            />
          </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Grid size={12} sx={{ mt: 4 }}>
        <Button disabled={disabled} variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important', minWidth: '110px', marginRight: '25px' }} onClick={handleNext}>
          SEND ORDER
        </Button>
        <Button disabled={disabled} variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important', minWidth: '110px' }} onClick={handleCancel}>
          CANCEL ORDER
        </Button>
        
      </Grid>
    </DashboardLayout>
    </div>
  )
}

export default OnlineOrderEntryOrder